import omit from "lodash-es/omit";
import { computed, observable, makeObservable, action } from "mobx";
import { inject, observer } from "mobx-react";
import { Component } from "react";
import { FormattedMessage } from "react-intl";
import { compose } from "nvent-web/utils/compose";

import { SecondaryBlueButton } from "nvent-web/components/Button";
import { ProductsStore } from "nvent-web/stores/Products";
import { type DropdownOption } from "nvent-web/types/DropdownOption";
import type {
  CategoryRecommendationVariant,
  ProductRecommendation,
} from "nvent-web/types/ProductRecommendation";

import ProductRecommendationCard from "../../components/ProductRecommendationCard/ProductRecommendationCard";

import { DownloadBOMModal } from "./components/DownloadBOMModal";
import style from "./RecommendationCard.module.scss";
import ThermostatModal from "./ThermostatModal";

interface RecommendationCardProps {
  recommendation: CategoryRecommendationVariant;
  onDownloadBom: (values: {
    products: CategoryRecommendationVariant;
    comments: string;
  }) => void;
  onCreateProject?: (recommendation: CategoryRecommendationVariant) => void;
  thermostatSkuOptions: DropdownOption[];
  countryCode?: string;
}

interface RecommendationCardInnerProps extends RecommendationCardProps {
  products: ProductsStore;
}

class RecommendationCardInner extends Component<RecommendationCardInnerProps> {
  private thermostatModalOpen = false;

  private thermostatId?: string;

  private showBOMModal = false;

  private products;

  constructor(props: RecommendationCardInnerProps) {
    super(props);

    this.products = props.products;

    makeObservable<
      RecommendationCardInner,
      "thermostatModalOpen" | "thermostatId" | "showBOMModal"
    >(this, {
      thermostatModalOpen: observable,
      thermostatId: observable,
      showBOMModal: observable,
      thermostat: computed,
      recommendationWithThermostat: computed,
      setThermostatModalOpen: action.bound,
      setThermostatId: action.bound,
      setShowBOMModal: action.bound,
    });
  }
  get thermostat(): ProductRecommendation | undefined {
    if (!this.thermostatId) {
      return undefined;
    }

    const thermostatSku = this.products.productSkus.find(
      (productSku) => productSku.id === Number(this.thermostatId)
    );

    if (!thermostatSku) {
      return undefined;
    }

    const thermostatRecommendation = {
      ...omit(thermostatSku, ["seNumber", "noNumber", "customLength"]),
      quantity: 1,
    };

    return thermostatRecommendation;
  }

  get recommendationWithThermostat(): CategoryRecommendationVariant {
    const { recommendation } = this.props;

    if (!this.thermostat) {
      return recommendation;
    }

    const accessorySkus = recommendation.accessorySkus
      ? [...recommendation.accessorySkus, this.thermostat]
      : [this.thermostat];

    return {
      ...recommendation,
      accessorySkus,
    };
  }

  setShowBOMModal(value: typeof this.showBOMModal) {
    this.showBOMModal = value;
  }

  setThermostatId(value: typeof this.thermostatId) {
    this.thermostatId = value;
  }

  setThermostatModalOpen(value: typeof this.thermostatModalOpen) {
    this.thermostatModalOpen = value;
  }

  render() {
    const { onCreateProject, recommendation } = this.props;

    return (
      <>
        <ProductRecommendationCard
          recommendation={recommendation}
          additionalThermostat={this.thermostat}
          onRemoveAdditionalThermostat={this.removeThermostat}
          actions={
            <div className={style.actionsContainer}>
              <SecondaryBlueButton
                className={style.actionButton}
                onClick={this.openThermostatPicker}
              >
                {this.thermostat ? (
                  <FormattedMessage id="selectionGuide.recommendations.changeThermostat" />
                ) : (
                  <FormattedMessage id="selectionGuide.recommendations.addThermostat" />
                )}
              </SecondaryBlueButton>
              {onCreateProject && (
                <SecondaryBlueButton
                  className={style.actionButton}
                  onClick={this.createProject}
                >
                  <FormattedMessage id="actions.createProject" />
                </SecondaryBlueButton>
              )}
              <SecondaryBlueButton
                className={style.actionButton}
                onClick={this.openBOMModal}
              >
                <FormattedMessage id="selectionGuide.getBOMButton" />
              </SecondaryBlueButton>
            </div>
          }
        />
        <ThermostatModal
          center
          isOpen={this.thermostatModalOpen}
          handleClose={this.closeThermostatPicker}
          onSubmit={this.addThermostat}
          thermostatSkuOptions={this.props.thermostatSkuOptions}
        />
        <DownloadBOMModal
          handleClose={this.closeBOMModal}
          isOpen={this.showBOMModal}
          center
          onSubmit={this.downloadBOM}
        />
      </>
    );
  }

  private downloadBOM = ({ comments }: { comments: string }) => {
    this.props.onDownloadBom({
      products: this.recommendationWithThermostat,
      comments,
    });
    this.closeBOMModal();
  };

  private createProject = () => {
    const { onCreateProject } = this.props;

    if (onCreateProject) {
      onCreateProject(this.recommendationWithThermostat);
    }
  };

  private openThermostatPicker = () => {
    this.setThermostatModalOpen(true);
  };

  private closeThermostatPicker = () => {
    this.setThermostatModalOpen(false);
  };

  private addThermostat = ({ productSkuId }: { productSkuId: string }) => {
    this.setThermostatId(productSkuId);
    this.closeThermostatPicker();
  };

  private removeThermostat = () => {
    this.setThermostatId(undefined);
  };

  private openBOMModal = () => {
    this.setShowBOMModal(true);
  };

  private closeBOMModal = () => {
    this.setShowBOMModal(false);
  };
}

export const RecommendationCard = compose<RecommendationCardProps>(
  inject("products"),
  observer
)(RecommendationCardInner);
